import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Terminal, Menu, X } from "lucide-react";
import { useSite, githubHandle } from "../lib/site";
import { EditableText } from "./EditableText";

const NAV_LINKS = [
  { to: "/", label: "Home", key: "nav_link_home", testid: "nav-home-link" },
  { to: "/about", label: "About", key: "nav_link_about", testid: "nav-about-link" },
  { to: "/projects", label: "Projects", key: "nav_link_projects", testid: "nav-projects-link" },
  { to: "/admin", label: "Admin Workspace", key: "nav_link_admin", testid: "nav-admin-link" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const { site } = useSite();

  const linkClass = ({ isActive }) =>
    `transition-colors hover:text-cyber ${isActive ? "text-cyber" : ""}`;

  return (
    <header className="border-b border-white/10 bg-ink/95 sticky top-0 z-50 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <nav className="flex items-center gap-6" aria-label="Main navigation">
          <Link to="/" className="flex items-center gap-2.5 text-white" data-testid="nav-brand-link">
            <Terminal className="w-6 h-6 text-cyber" strokeWidth={2} />
            <span className="font-mono font-bold tracking-tight text-sm text-white">
              <EditableText k="nav_brand" defaultValue="oVu Cyber" editTriggerOnly />
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-5 font-mono text-xs text-dim">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} className={linkClass} data-testid={l.testid}>
                <EditableText k={l.key} defaultValue={l.label} editTriggerOnly />
              </NavLink>
            ))}
          </div>
        </nav>
        <div className="flex items-center gap-3">
          <a
            href={site.github_url}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-block font-mono text-[11px] px-2.5 py-1 rounded bg-cyber/10 text-cyber border border-cyber/30 hover:bg-cyber/20 transition-colors"
            data-testid="nav-github-chip"
          >
            <EditableText k="nav_github_prefix" defaultValue="github:" editTriggerOnly /> {githubHandle(site.github_url)}
          </a>
          <Link
            to="/#contact"
            className="px-3.5 py-1.5 rounded bg-cyber text-[#0b0f19] font-bold text-xs font-mono hover:bg-white transition-colors"
            data-testid="nav-connect-button"
          >
            <EditableText k="nav_connect_button" defaultValue="Connect" editTriggerOnly />
          </Link>
          <button
            type="button"
            className="md:hidden text-mist hover:text-cyber transition-colors"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-menu-button"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/10 bg-ink px-6 py-4 flex flex-col gap-3 font-mono text-xs text-dim" data-testid="nav-mobile-menu">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `py-1 transition-colors hover:text-cyber ${isActive ? "text-cyber" : ""}`}
              data-testid={`${l.testid}-mobile`}
            >
              <EditableText
                k={l.key}
                defaultValue={l.label}
                editTriggerOnly
                testId={`editable-${l.key}-mobile`}
              />
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}
